import React, { useEffect, useState, useCallback } from 'react';

const emptyForm = {
  category_code: '',
  category_name: '',
  parent_id: '',
  alert_cap_amount: '',
  default_is_itc_eligible: true,
  is_active: true
};

function PmsExpenseCategory({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    try {
      const res = await fetch('/api/v1/expense/categories', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load');
      setRows(data.rows || []);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = () => { setForm(emptyForm); setEditingId(''); };

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    if (!form.category_code || !form.category_name) return setError('Code and name required');
    setError('');
    setMessage('');
    try {
      const payload = {
        ...form,
        parent_id: form.parent_id || null,
        alert_cap_amount: form.alert_cap_amount === '' ? null : Number(form.alert_cap_amount)
      };
      const url = editingId ? `/api/v1/expense/categories/${editingId}` : '/api/v1/expense/categories';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Category updated.' : 'Category created.');
      reset();
      loadData();
    } catch { setError('Network error'); }
  };

  const startEdit = (c) => {
    setForm({
      category_code: c.category_code,
      category_name: c.category_name,
      parent_id: c.parent_id || '',
      alert_cap_amount: c.alert_cap_amount || '',
      default_is_itc_eligible: c.default_is_itc_eligible !== false,
      is_active: c.is_active !== false
    });
    setEditingId(c.category_id);
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };
  const labelStyle = { display: 'grid', gap: '0.25rem', fontSize: '0.8rem' };
  const fmtMoney = (n) => n == null ? '—' : `₹ ${Number(n).toLocaleString('en-IN')}`;

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Expense Categories</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', marginBottom: '0.8rem' }}>
        <h3 style={{ margin: '0 0 0.7rem 0', fontSize: '1rem' }}>{editingId ? 'Edit Category' : 'New Category'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
          <label style={labelStyle}><span>Code *</span><input value={form.category_code} onChange={e => set('category_code', e.target.value.toUpperCase())} disabled={!!editingId} style={inputStyle} /></label>
          <label style={labelStyle}><span>Name *</span><input value={form.category_name} onChange={e => set('category_name', e.target.value)} style={inputStyle} /></label>
          <label style={labelStyle}><span>Parent Category</span>
            <select value={form.parent_id} onChange={e => set('parent_id', e.target.value)} style={inputStyle}>
              <option value="">— None —</option>
              {rows.filter(r => r.category_id !== editingId).map(r => <option key={r.category_id} value={r.category_id}>{r.category_name}</option>)}
            </select>
          </label>
          <label style={labelStyle}><span>Alert Cap Amount</span><input type="number" step="0.01" value={form.alert_cap_amount} onChange={e => set('alert_cap_amount', e.target.value)} style={inputStyle} /></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input type="checkbox" checked={!!form.default_is_itc_eligible} onChange={e => set('default_is_itc_eligible', e.target.checked)} />
            <span>ITC eligible by default</span>
          </label>
          {editingId && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} />
              <span>Active</span>
            </label>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.8rem' }}>
          {editingId && <button onClick={reset} style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer' }}>Cancel</button>}
          <button onClick={save} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {editingId ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: '0.55rem' }}>Code</th>
              <th style={{ padding: '0.55rem' }}>Name</th>
              <th style={{ padding: '0.55rem' }}>Parent</th>
              <th style={{ padding: '0.55rem' }}>ITC</th>
              <th style={{ padding: '0.55rem', textAlign: 'right' }}>Alert Cap</th>
              <th style={{ padding: '0.55rem' }}>Source</th>
              <th style={{ padding: '0.55rem' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No categories yet — run the seeder from Admin or create one.</td></tr>
            ) : rows.map(c => (
              <tr key={c.category_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{c.category_code}</td>
                <td style={{ padding: '0.5rem' }}>{c.category_name}</td>
                <td style={{ padding: '0.5rem', color: '#6b7280' }}>{rows.find(r => r.category_id === c.parent_id)?.category_name || '—'}</td>
                <td style={{ padding: '0.5rem' }}>{c.default_is_itc_eligible ? '✓' : '—'}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(c.alert_cap_amount)}</td>
                <td style={{ padding: '0.5rem' }}>{c.is_seeded ? <span style={{ fontSize: '0.72rem', padding: '1px 6px', borderRadius: 4, background: '#e0e7ff', color: '#3730a3' }}>Seeded</span> : 'Custom'}</td>
                <td style={{ padding: '0.5rem' }}><button onClick={() => startEdit(c)} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsExpenseCategory;
